import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import { getAllCommandes, confirmCommande, cancelCommande } from '../services/commandeService';
import { Eye } from 'lucide-react';
import swal from 'sweetalert';
import dayjs from 'dayjs';

const CommandesScreen = () => {
  const { slug } = useParams();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRef, setSelectedRef] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await getAllCommandes();
      const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setOrders(sorted);
    } catch {}
    setTimeout(() => setLoading(false), 600);
  };

  const handleAction = async (id, type) => {
    setLoading(true);
    try {
      if (type === 'confirm') {
        await confirmCommande(id);
      } else {
        await cancelCommande(id);
      }
      await fetchOrders();
      swal(`✅ Commande ${type === 'confirm' ? 'confirmée' : 'annulée'}`, '', 'success');
    } catch (error) {
      const msg = error?.response?.data?.message || 'Une erreur est survenue';
      const productImg = error?.response?.data?.image;
      if (productImg) {
        swal({
          title: '❌ Erreur',
          text: msg,
          icon: productImg,
        });
      } else {
        swal('❌ Erreur', msg, 'error');
      }
    }
    setLoading(false);
  };

  const filtered = orders.filter((order) => {
    const term = search.toLowerCase();
    const fullName = `${order.firstName} ${order.lastName}`.toLowerCase();
    const phone = order.phoneNumber?.toLowerCase() || '';
    const ref = order.reference?.toLowerCase() || '';
    const status = order.status?.toLowerCase() || '';
    const total = order.totalPrice.toFixed(2).toString();
    const productNames = order.items.map(i => i.product?.name?.toLowerCase() || '').join(' ');

    const matchesSearch =
      fullName.includes(term) ||
      phone.includes(term) ||
      ref.includes(term) ||
      status.includes(term) ||
      total.includes(term) ||
      productNames.includes(term);

    const orderDate = dayjs(order.createdAt);
    const afterStart = startDate ? orderDate.isAfter(dayjs(startDate).startOf('day')) || orderDate.isSame(dayjs(startDate).startOf('day')) : true;
    const beforeEnd = endDate ? orderDate.isBefore(dayjs(endDate).endOf('day')) || orderDate.isSame(dayjs(endDate).endOf('day')) : true;

    return matchesSearch && afterStart && beforeEnd;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="flex min-h-screen">
      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-6 mt-16 md:mt-0 bg-gray-50">
        <div className="container mx-auto mt-4">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-paragaia">Toutes les Commandes</h1>
            <button onClick={fetchOrders} className="bg-pink-200 px-4 py-2 rounded hover:bg-pink-300 text-sm">
              Rafraîchir
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <input
              type="text"
              placeholder="Rechercher par client, référence, produit..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="p-3 border rounded"
            />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="p-3 border rounded"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="p-3 border rounded"
            />
          </div>
          <div className="flex justify-end mb-4">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="p-2 rounded text-white bg-paragaia"
            >
              {[10, 25, 50].map((num) => (
                <option key={num} value={num} className="text-black">
                  Afficher {num}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto hidden md:block">
            <table className="w-full text-left text-sm text-gray-700">
              <thead className="bg-paragaia text-white">
                <tr>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Client</th>
                  <th className="py-3 px-4 text-center">Référence</th>
                  <th className="py-3 px-4">Produits</th>
                  <th className="py-3 px-4">Total</th>
                  <th className="py-3 px-4">Statut</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((order, index) => (
                  <tr key={order._id} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}>
                    <td className="py-3 px-4">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="py-3 px-4">
                      <p>{order.firstName} {order.lastName}</p>
                      <p className="text-xs text-gray-500">{order.phoneNumber}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center items-center gap-2">
                        <span>{order.reference.slice(0, 8)}...</span>
                        <button onClick={() => setSelectedRef(order.reference)}>
                          <Eye size={16} className="text-paragaia hover:text-paragaiaLight" />
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="space-y-2">
                        {order.items.map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <img src={item.product?.image} alt="" className="w-8 h-8 rounded object-cover" />
                            <span className="text-sm">{item.product?.name}</span>
                            <span className="text-xs text-gray-500">x{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4">{order.totalPrice.toFixed(2)} TND</td>
                    <td className="py-3 px-4">
                      <span className={`capitalize text-xs font-semibold px-2 py-1 rounded-full ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{new Date(order.createdAt).toLocaleString()}</td>
                    <td className="py-3 px-4">
                      {order.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAction(order._id, 'confirm')}
                            className="bg-green-100 hover:bg-green-200 px-3 py-1 rounded text-sm w-full"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => handleAction(order._id, 'cancel')}
                            className="bg-red-100 hover:bg-red-200 px-3 py-1 rounded text-sm w-full"
                          >
                            Annuler
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {paginated.map((order) => (
              <div key={order._id} className="bg-white rounded-lg shadow p-4 space-y-2">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {order.status}
                  </span>
                </div>
                <div>
                  <p className="font-semibold">{order.firstName} {order.lastName}</p>
                  <p className="text-xs text-gray-500">{order.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Référence:</p>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span>{order.reference.slice(0, 10)}...</span>
                    <button onClick={() => setSelectedRef(order.reference)}>
                      <Eye size={16} className="text-paragaia hover:text-paragaiaLight" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Produits:</p>
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <img src={item.product?.image} alt="" className="w-8 h-8 rounded object-cover" />
                      <span className="text-sm">{item.product?.name}</span>
                      <span className="text-xs text-gray-500">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="text-sm font-semibold">Total: {order.totalPrice.toFixed(2)} TND</div>
                {order.status === 'pending' && (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleAction(order._id, 'confirm')}
                      className="bg-green-100 hover:bg-green-200 px-3 py-1 rounded text-sm flex-1"
                    >
                      Confirmer
                    </button>
                    <button
                      onClick={() => handleAction(order._id, 'cancel')}
                      className="bg-red-100 hover:bg-red-200 px-3 py-1 rounded text-sm flex-1"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            ))}
            {paginated.length === 0 && (
              <p className="text-center text-gray-500 py-6">Aucune commande trouvée.</p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-1 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &lt;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (page === 1 || page === totalPages) return true;
                  if (page >= currentPage - 2 && page <= currentPage + 2) return true;
                  return false;
                })
                .map((page, i, arr) => {
                  const prev = arr[i - 1];
                  const isGap = prev && page - prev > 1;
                  return (
                    <React.Fragment key={page}>
                      {isGap && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${
                          currentPage === page
                            ? 'bg-paragaia text-white'
                            : 'bg-white text-paragaia hover:bg-paragaiaLight'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &gt;
              </button>
            </div>
          )}

          {selectedRef && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-md w-96 text-center">
                <h2 className="text-lg font-semibold mb-2 text-paragaia">Référence complète</h2>
                <p className="text-sm text-gray-800 break-all">{selectedRef}</p>
                <button
                  onClick={() => setSelectedRef(null)}
                  className="mt-4 px-4 py-2 bg-paragaia text-white rounded hover:bg-paragaia/90"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
          {loading && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="p-4 rounded">
                <img src="/assets/loading.gif" alt="Loading..." className="w-16 h-16" />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CommandesScreen;
